import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first, map } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';

import { AnalyticsService } from '../../shared/services/analytics.service';
import { ChatStateService } from '../../shared/services/chat-state.service';
import { ChatListService } from '../../shared/services/chat-list.service';
import { MediaService } from '../../shared/services/media.service';
import { TokensService } from 'app/shared/services/tokens.service';
import { ProfileStateService } from '../../shared/services/profile-state.service';

import { AuthFormComponent } from 'app/shared/components/auth-form/auth-form.component';
import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';

import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';

import { EButtonActionTypes } from '../../shared/enums/button.enums';
import { ELocalStorageKey } from '../../shared/enums/shared.enums';
import { ESvgTypes } from 'app/shared/enums/svg.enums';

@Component({
  selector: 'stxt-navigation-bar',
  standalone: true,
  imports: [CommonModule, RouterModule, SvgIconComponent],
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.scss'
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  public chatSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'chat-outlined' };
  public chatFilledSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'chat-filled' };
  public tokenSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'tokens-package' };
  public generateSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'generate-image' };
  public generateFilledSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'generate-image-filled' };
  public homeSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'home' };
  public homeFilledSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'home-filled' };
  public isChatRouteActive: boolean = false;
  public isHomeRouteActive: boolean = false;
  public unreadMessagesCount$: Observable<number>;
  public unreadGenerationsCount: number = 0;
  public pageUrl: string;

  private routerSubscription: Subscription;
  private chatStateSubscription: Subscription;
  private unreadGenerationsSubscription: Subscription;
  private previousUrl: string;

  constructor(
    private readonly router: Router,
    private readonly chatListService: ChatListService,
    private readonly analyticsService: AnalyticsService,
    private readonly mediaService: MediaService,
    private readonly modal: NgbModal,
    private readonly chatStateService: ChatStateService,
    private readonly profileStateService: ProfileStateService,
    private readonly tokensService: TokensService
  ) {}

  ngOnInit(): void {
    this.subscribeToRouterEvents();
    this.trackUnreadGenerations();
    this.initChatConnection();
    this.initMediaAndProfileData();
  }

  subscribeToRouterEvents(): void {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.pageUrl;
        this.pageUrl = event.urlAfterRedirects;

        this.isChatRouteActive = this.pageUrl?.includes('/chat');
        this.isHomeRouteActive = this.pageUrl === '/' || this.pageUrl.startsWith('/?');

        // Reset unread notifications counter when navigating AWAY from generate page
        if (this.previousUrl === '/generate' && this.pageUrl !== '/generate') {
          this.mediaService.resetUnreadCounter();
        }

        this.handleChatPageNavigation();
      }
    });
  }

  initMediaAndProfileData(): void {
    const userId = localStorage.getItem(ELocalStorageKey.UserId);
    if (!userId) return;
    this.mediaService.initialLoad().pipe(first()).subscribe();
    this.profileStateService.prefetchProfileData().pipe(first()).subscribe();
    this.setupUnreadMessagesCounter();
  }

  setupUnreadMessagesCounter(): void {
    this.unreadMessagesCount$ = this.chatStateService.unreadMessages$.pipe(
      map(unreadMessages => {
        const counts = Object.values(unreadMessages);
        return counts.reduce((sum, count) => sum + count, 0);
      })
    );
  }

  private handleChatPageNavigation(): void {
    if (!this.pageUrl?.includes('/chat')) return;

    const conversationId = this.chatListService.selectedConversationId.value;
    if (!conversationId) return;

    this.chatStateService.clearUnreadMessages(conversationId);
  }

  initChatConnection(): void {
    const userId = localStorage.getItem(ELocalStorageKey.UserId);
    const sessionToken = localStorage.getItem('accessToken');

    if (!userId || !sessionToken) return;

    this.chatListService.conversations$.pipe(first()).subscribe(conversations => {
      if (!conversations || conversations.length === 0) return;

      const lastConversation = conversations[0];
      const creatorId = lastConversation.conversation_id.split('-')[0];
      this.chatStateService.connectToChat(sessionToken, creatorId);
    });
  }

  trackUnreadGenerations(): void {
    this.unreadGenerationsSubscription = this.mediaService.unreadCompletedGenerations$.subscribe(
      count => (this.unreadGenerationsCount = count)
    );
  }

  openChat(): void {
    const currentUrl = this.router.url;
    const isMobile = window.innerWidth <= 640;
    const isInChat = currentUrl.includes('/chat');

    if (isInChat && isMobile) {
      this.chatListService.isChatsFullScreen.next(true);
      const currentState = history.state;
      const newState = { ...currentState, chatList: true };
      history.replaceState(newState, '', this.router.url);
      return;
    } else if (!isInChat && isMobile) {
      this.trackCTAEvent('Chat', '/chat');
      this.router.navigateByUrl('/chat', { state: { chatList: true } }).then(() => {
        this.chatListService.isChatsFullScreen.next(true);
      });
      return;
    }

    if (isInChat) return;

    this.trackCTAEvent('Chat', '/chat');
    this.router.navigateByUrl('/chat');
  }

  toggleUnlock(): void {
    if (localStorage.getItem(ELocalStorageKey.UserId)) {
      this.tokensService.getMoreTokensEvent$.next(true);
    } else {
      const modalRef = this.modal.open(AuthFormComponent);
      modalRef.componentInstance.isSignUp = true;
    }
  }

  getPageName(url: string): string {
    if (!url) {
      return 'Home';
    }
    // Implement logic to derive page name from URL
    const segments = url.split('/');
    return segments[segments.length - 1] || 'Home';
  }

  trackCTAEvent(clickText: string, url: string): void {
    const pageName = this.getPageName(this.pageUrl);
    this.analyticsService.click(clickText, url, pageName, EButtonActionTypes.HeaderNavigation, url);
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.unreadGenerationsSubscription?.unsubscribe();
    this.chatStateSubscription?.unsubscribe();
  }
}
